import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  IconButton,
  Tooltip,
  LinearProgress,
  Chip,
  CircularProgress,
} from "@mui/material";
import { Fade } from "@mui/material";
import DatasetIcon from "@mui/icons-material/Dataset";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { useNavigate } from "react-router-dom";
import QuestionNavigation from "./QuestionNavigation";
import RecommendationComponent from "./RecommendationComponent";
import DynamicMathChart from "./DynamicMathChart";
import { MathJaxContext, MathJax } from "better-react-mathjax";
import Voice from "./Voice";
import Logger from "./Logger";
import GridViewIcon from "@mui/icons-material/GridView";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import RadioButtonCheckedIcon from "@mui/icons-material/RadioButtonChecked";
import LooksOneIcon from "@mui/icons-material/LooksOne";
import CloseIcon from "@mui/icons-material/Close";
import NumbersIcon from "@mui/icons-material/Numbers";
import LooksTwoIcon from "@mui/icons-material/LooksTwo";
import KeyIcon from "@mui/icons-material/Key";
import Looks3Icon from "@mui/icons-material/Looks3";
import Looks4Icon from "@mui/icons-material/Looks4";
import Looks5Icon from "@mui/icons-material/Looks5";
//import OptionButton from "./OptionButton";'
import WelcomeDialog from "./WelcomeDialog";

import { OptionButton } from "../StyledComponents";
import { RotateAnimation, BounceAnimation } from "./Animations";
import SegmentIcon from "@mui/icons-material/Segment";

const TestInProgressComponent = ({
  hpTest,
  currentHpName,
  currentSectionIndex,
  currentQuestionIndex,
  focusModeActive,
  toggleFocusMode,
  timeLeft,
  timeLeftRef,
  timerDisplayRef,
  selectedOption,
  handleOptionSelect,
  handleOpenOverview,
  handlePreviousQuestion,
  handleNextQuestion,
  handleSaveQuestion,
  handleAskAI,
  handleSubmitTest,
  isSaved,
  results,
  testId,
  user,
  theme,
  styles,
  isMobile,
  setDrawerOpen,
  showSnackbar,
}) => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(true);
  const [showCodeOverlay, setShowCodeOverlay] = useState(null);

  const handleClose = () => {
    setOpen(false);
  };

  const handleOpen = () => {
    setOpen(true);
  };

  const renderQuestion = () => {
    if (
      currentSectionIndex >= hpTest.length ||
      currentQuestionIndex >= hpTest[currentSectionIndex].questions.length
    ) {
      return null;
    }

    // Add this to the top of your component, inside the TestInProgressComponent function
    const [isRevealed, setIsRevealed] = useState(false);

    // Add this useEffect to trigger animation when question changes
    useEffect(() => {
      setIsRevealed(false);
      const timer = setTimeout(() => {
        setIsRevealed(true);
      }, 50);
      return () => clearTimeout(timer);
    }, [currentSectionIndex, currentQuestionIndex]);

    const currentSection = hpTest[currentSectionIndex];
    const currentQuestion = currentSection.questions[currentQuestionIndex];

    const optionIcons = [
      RadioButtonUncheckedIcon,
      RadioButtonUncheckedIcon,
      RadioButtonUncheckedIcon,
      RadioButtonUncheckedIcon,
      RadioButtonUncheckedIcon,
    ];

    // const optionIcons = [
    //   LooksOneIcon,
    //   LooksTwoIcon,
    //   Looks3Icon,
    //   Looks4Icon,
    //   Looks5Icon,
    // ];

    let width = "50%";
    if (
      currentSection.questionType === "LÄS" ||
      currentSection.questionType === "ELF"
    ) {
      width = "100%";
    }

    const renderOptions = () => (
      <>
        <MathJaxContext
          config={{
            tex: {
              inlineMath: [
                ["$", "$"],
                ["\\(", "\\)"],
              ],
              displayMath: [
                ["$$", "$$"],
                ["\\[", "\\]"],
              ],
            },
            chtml: {
              scale: 1.3,
              fontFamily: "Arial",
              matchFontHeight: false,
              displayAlign: "center",
              displayIndent: "0",
            },
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              width: { xs: "100%", md: width },
            }}
          >
            {currentQuestion.options.map((option, index) => {
              const OptionIcon =
                selectedOption === option
                  ? RadioButtonCheckedIcon
                  : optionIcons[index];

              return (
                <OptionButton
                  key={index}
                  variant={selectedOption === option ? "contained" : "outlined"}
                  onClick={() => handleOptionSelect(option)}
                  sx={{ width: "100%" }}
                  translate="no"
                >
                  <OptionIcon
                    className="optionIcon"
                    sx={{ marginRight: "8px", color: "#8b5cf6" }}
                  />
                  <span>
                    <MathJax> {option}</MathJax>
                  </span>
                </OptionButton>
              );
            })}
          </Box>
        </MathJaxContext>
      </>
    );

    if (currentSection.questionType === "DTK") {
      // Render chart-based question
      return (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            width: "100%",
          }}
        >
          <Box
            sx={{
              mb: 3,
              mt: 4,
              textAlign: "center",
              border:
                theme.palette.mode === "dark"
                  ? "1px solid #4a5d6b"
                  : "1px solid #eaeaea",
              fontWeight: "bold",
              borderRadius: "20px",
              backgroundColor:
                theme.palette.mode === "dark" ? "rgb(25, 39, 52)" : "white",
              width: { xs: "100%", md: "50%" },
              padding: "40px",
              color: theme.palette.mode === "dark" ? "white" : "#333",
            }}
          >
            <Typography variant="body1" sx={{ marginBottom: "25px" }}>
              {currentQuestion.question}
            </Typography>
            <DynamicMathChart
              chartType={currentQuestion.chartType}
              chartData={currentQuestion.chartData}
              chartConfig={currentQuestion.chartConfig}
            />
          </Box>

          {renderOptions()}
          <div
            className={`focus-toggle ${focusModeActive ? "focus-active" : ""}`}
          >
            <QuestionNavigation
              handlePreviousQuestion={handlePreviousQuestion}
              handleNextQuestion={handleNextQuestion}
              handleSaveQuestion={handleSaveQuestion}
              handleAskAI={handleAskAI}
              isSaved={isSaved}
              currentQuestionIndex={currentQuestionIndex}
              currentSectionIndex={currentSectionIndex}
              theme={theme}
              styles={styles}
            />
            <Box
              sx={{
                width: { xs: "100%", md: "80%", margin: "0 auto" },
                display: "none",
              }}
            >
              <RecommendationComponent
                itemId={testId}
                userId={user.uid}
                collectionName="hpPublic"
                questionText="Skulle du rekommendera detta högskoleprov till andra användare?"
              />
            </Box>
          </div>
        </Box>
      );
    }

    const convertToLatex = (text) => {
      if (!text) return text;

      // Already handled by MathJax if properly delimited
      if (text.includes("$") && text.split("$").length > 2) {
        return text;
      }

      // Directly wrap the equation in math delimiters if it contains LaTeX commands
      if (
        text.includes("\\frac") ||
        text.includes("\\sqrt") ||
        text.includes("\\sum") ||
        text.includes("\\int")
      ) {
        return `$${text}$`;
      }

      return text;
    };
    if (currentSection.questionType === "XYZ") {
      //const sanitizedEquation = convertToLatex(currentQuestion.equation);
      //Logger.log("Sanitized equation:", sanitizedEquation);

      // Kontrollera om ekvationen är lång (t.ex. över 50 tecken)
      //const isLongEquation = sanitizedEquation.length > 50;

      // Funktion för att konvertera ekvationen till vertikalt format
      const convertToVertical = (eq) => {
        return `\\begin{align*}\n${eq.replace(/=/g, "&=")}\n\\end{align*}`;
      };

      // const displayEquation = isLongEquation
      //   ? convertToVertical(sanitizedEquation)
      //   : sanitizedEquation;

      return (
        <MathJaxContext
          config={{
            tex: {
              inlineMath: [
                ["$", "$"],
                ["\\(", "\\)"],
              ],
              displayMath: [
                ["$$", "$$"],
                ["\\[", "\\]"],
              ],
            },
            chtml: {
              scale: 1.3,
              fontFamily: "Arial",
              matchFontHeight: false,
              displayAlign: "center",
              displayIndent: "0",
            },
          }}
        >
          <BounceAnimation
            isRevealed={isRevealed}
            key={`question-${currentSectionIndex}-${currentQuestionIndex}`}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                width: "100%",
              }}
            >
              <Box
                sx={{
                  mb: 3,
                  mt: 4,
                  textAlign: "center",
                  border:
                    theme.palette.mode === "dark"
                      ? "1px solid #4a5d6b"
                      : "1px solid #eaeaea",
                  fontWeight: "bold",
                  borderRadius: "20px",
                  backgroundColor:
                    theme.palette.mode === "dark" ? "rgb(25, 39, 52)" : "white",
                  width: { xs: "100%", md: "50%" },
                  padding: "40px",
                  color: theme.palette.mode === "dark" ? "white" : "#333",
                }}
              >
                <Typography
                  variant="body1"
                  sx={{ fontSize: { xs: "1rem", md: "1.2rem" } }}
                >
                  <MathJax>{currentQuestion.question}</MathJax>
                </Typography>
              </Box>
              {renderOptions()}
              <Box
                className={`focus-toggle ${focusModeActive ? "focus-active" : ""}`}
                sx={{ width: "50%" }}
              >
                <QuestionNavigation
                  handlePreviousQuestion={handlePreviousQuestion}
                  handleNextQuestion={handleNextQuestion}
                  handleSaveQuestion={handleSaveQuestion}
                  handleAskAI={handleAskAI}
                  isSaved={isSaved}
                  currentQuestionIndex={currentQuestionIndex}
                  currentSectionIndex={currentSectionIndex}
                  theme={theme}
                  styles={styles}
                />

                <Box
                  sx={{
                    width: {
                      xs: "100%",
                      md: "auto",
                      margin: "0 auto",
                      textAlign: "center",
                      display: "none",
                    },
                  }}
                >
                  <RecommendationComponent
                    itemId={testId}
                    userId={user.uid}
                    collectionName="hpPublic"
                    questionText="Skulle du rekommendera detta högskoleprov till andra användare?"
                  />
                </Box>
              </Box>
            </Box>
          </BounceAnimation>
        </MathJaxContext>
      );
    } else if (
      currentSection.questionType === "LÄS" ||
      currentSection.questionType === "ELF"
    ) {
      return (
        <>
          <BounceAnimation
            isRevealed={isRevealed}
            key={`question-${currentSectionIndex}-${currentQuestionIndex}`}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", sm: "row" },
                width: "100%",
                p: { xs: 0, md: 5 },
              }}
            >
              <Box
                sx={{
                  flex: { xs: "0 0 100%", sm: 1 },
                  pr: { xs: 0, sm: 2 },
                  mb: { xs: 2, sm: 0 },
                }}
              >
                <Typography
                  variant="body1"
                  sx={{
                    whiteSpace: "pre-wrap",
                    lineHeight: "30px",
                    marginTop: hpTest.official ? "0px" : "30px",
                  }}
                >
                  <span
                    dangerouslySetInnerHTML={{ __html: currentSection.text }}
                  />
                </Typography>
              </Box>
              <Box
                sx={{
                  flex: { xs: "0 0 100%", sm: 1 },
                  pl: { xs: 0, sm: 2 },
                }}
              >
                <Typography variant="h6" sx={{ mb: 3, mt: 3 }}>
                  {currentQuestion.question}
                </Typography>
                {renderOptions()}

                <Box
                  className={`focus-toggle ${focusModeActive ? "focus-active" : ""}`}
                  sx={{ width: "100%" }}
                >
                  <QuestionNavigation
                    handlePreviousQuestion={handlePreviousQuestion}
                    handleNextQuestion={handleNextQuestion}
                    handleSaveQuestion={handleSaveQuestion}
                    handleAskAI={handleAskAI}
                    isSaved={isSaved}
                    currentQuestionIndex={currentQuestionIndex}
                    currentSectionIndex={currentSectionIndex}
                    theme={theme}
                    styles={styles}
                  />

                  <Box
                    sx={{
                      width: {
                        xs: "100%",
                        md: "auto",
                        margin: "0 auto",
                        textAlign: "center",
                        display: "none",
                      },
                    }}
                  >
                    <RecommendationComponent
                      itemId={testId}
                      userId={user.uid}
                      collectionName="hpPublic"
                      questionText="Skulle du rekommendera detta högskoleprov till andra användare?"
                    />
                  </Box>
                </Box>
              </Box>
            </Box>
          </BounceAnimation>
        </>
      );
    } else {
      return (
        <>
          <BounceAnimation
            isRevealed={isRevealed}
            key={`question-${currentSectionIndex}-${currentQuestionIndex}`}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                width: "100%",
              }}
            >
              <Box
                sx={{
                  mb: 3,
                  mt: { xs: 2, md: 7 },
                  textAlign: "center",
                  position: "relative",
                  fontSize: "1.2rem",
                  border:
                    theme.palette.mode === "dark"
                      ? "1px solid rgb(34, 48, 60)"
                      : "1px solid #eaeaea",
                  fontWeight: "600",
                  borderRadius: "20px",
                  backgroundColor:
                    theme.palette.mode === "dark" ? "rgb(25, 39, 52)" : "white",
                  width: { xs: "100%", md: "50%" },
                  padding: "40px",
                  color: theme.palette.mode === "dark" ? "white" : "#333",
                }}
              >
                <Box
                  sx={{
                    position: "absolute",
                    top: 7,
                    right: 7,
                    zIndex: 1,
                    display: "none",
                  }}
                >
                  <Voice
                    key={`${currentSectionIndex}-${currentQuestionIndex}`}
                    content={currentQuestion.question}
                    fontSize={25}
                    userId={user.uid}
                  />
                </Box>

                {currentQuestion.question}
              </Box>
              {renderOptions()}
              <Box
                className={`focus-toggle ${focusModeActive ? "focus-active" : ""}`}
                sx={{ width: { xs: "100%", md: "50%" } }}
              >
                <QuestionNavigation
                  handlePreviousQuestion={handlePreviousQuestion}
                  handleNextQuestion={handleNextQuestion}
                  handleSaveQuestion={handleSaveQuestion}
                  handleAskAI={handleAskAI}
                  isSaved={isSaved}
                  currentQuestionIndex={currentQuestionIndex}
                  currentSectionIndex={currentSectionIndex}
                  theme={theme}
                  styles={styles}
                />

                <Box
                  sx={{
                    width: {
                      xs: "100%",
                      md: "auto",
                      margin: "0 auto",
                      textAlign: "center",
                      display: "none",
                    },
                  }}
                >
                  <RecommendationComponent
                    itemId={testId}
                    userId={user.uid}
                    collectionName="hpPublic"
                    questionText="Skulle du rekommendera detta högskoleprov till andra användare?"
                  />
                </Box>
              </Box>
            </Box>
          </BounceAnimation>
        </>
      );
    }
  };

  return (
    <>
      <Fade in={true} timeout={1000}>
        <Box sx={{ width: "100%", mt: 4, paddingRight: { xs: "0px" } }}>
          <Box sx={{ display: "none", justifyContent: "flex-end" }}>
            <Tooltip title="Fokusläge">
              <IconButton color="secondary" onClick={toggleFocusMode}>
                {focusModeActive ? (
                  <VisibilityOffIcon
                    sx={{
                      color:
                        theme.palette.mode === "dark" ? "#157dbd" : "#a4a3a4",
                    }}
                  />
                ) : (
                  <VisibilityIcon
                    sx={{
                      color:
                        theme.palette.mode === "dark" ? "#157dbd" : "#a4a3a4",
                    }}
                  />
                )}
              </IconButton>
            </Tooltip>
          </Box>
          <Box
            sx={{
              marginTop: "0px !important",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 2,
              ml: { xs: 1, md: 2 },
              margin: { xs: "15px", md: "inherit" },
              flexWrap: { xs: "wrap", md: "nowrap" },
            }}
          >
            {user.accountType === "teacher" && (
              <Chip
                className={`focus-toggle ${focusModeActive ? "focus-active" : ""}`}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setShowCodeOverlay(testId.substring(0, 6));
                }}
                label={
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 0.5,
                      pointerEvents: "auto !important",
                    }}
                  >
                    <KeyIcon sx={{ fontSize: 25, mr: 1 }} />
                    Dela med elever
                  </Box>
                }
                size="small"
                sx={{
                  ...styles.greenChip,
                  height: "50px",
                  cursor: "pointer",
                  fontSize: "1.2rem",
                  paddingLeft: "10px",
                  paddingRight: "10px",
                  pointerEvents: "auto !important",
                  position: "absolute",
                  bottom: 15,
                  left: 0,
                  zIndex: 9999999,
                  display: { xs: "none", md: "flex" },
                  "&:hover": {
                    backgroundColor: `${theme.palette.primary.main} !important`,
                    color: "white !important",
                  },
                }}
              />
            )}
            {/* Left section */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: { xs: "space-between", md: "flex-start" },
                gap: { xs: 0, md: 7 },
                order: { xs: 1, md: 1 },
                width: { xs: "100%", md: "auto" },
                mb: { xs: 2, md: 0 },
              }}
            >
              {/* Close button */}
              <Tooltip title="Stäng">
                <IconButton
                  sx={{ ml: { xs: 0, md: 2 } }}
                  onClick={(e) => {
                    navigate(-1);
                  }}
                  className={`focus-toggle ${focusModeActive ? "focus-active" : ""}`}
                >
                  <CloseIcon
                    sx={{
                      fontSize: { xs: "1.5rem", md: "2rem" },
                      color: "text.secondary",
                    }}
                  />
                </IconButton>
              </Tooltip>

              {/* Question count */}
              <Box
                sx={{ display: "flex", alignItems: "center", gap: 1 }}
                className={`focus-toggle ${focusModeActive ? "focus-active" : ""}`}
              >
                <NumbersIcon sx={{ color: "text.secondary" }} />
                <Typography
                  variant="body2"
                  sx={{
                    color: theme.palette.mode === "dark" ? "#c4c4c4" : "#333",
                  }}
                >
                  {currentQuestionIndex + 1} av{" "}
                  {hpTest[currentSectionIndex].questions.length}
                </Typography>
              </Box>
              {/* Overview button */}
              {isMobile ? (
                <Button
                  variant="print"
                  color="primary"
                  onClick={handleOpenOverview}
                  disableRipple
                  startIcon={<GridViewIcon />}
                  sx={{ ...styles.shrinkButton }}
                ></Button>
              ) : (
                <Button
                  variant="print"
                  color="primary"
                  onClick={handleOpenOverview}
                  sx={{
                    ml: 2,
                    fontWeight: 500,
                    borderRadius: 2,
                    p: 0.6,
                    pl: 4,
                    pr: 4,
                    fontSize: "0.9rem",
                  }}
                  className={`focus-toggle ${focusModeActive ? "focus-active" : ""}`}
                >
                  Översikt
                </Button>
              )}
            </Box>

            {/* Center - Question info */}
            <Typography
              variant="h6"
              sx={{
                color: theme.palette.mode === "dark" ? "#c4c4c4" : "#333",
                textAlign: "center",
                fontSize: { xs: "1.1rem", md: "1.4rem" },
                fontWeight: 500,
                order: { xs: 0, md: 2 },
                width: { xs: "100%", md: "auto" },
                mb: { xs: 2, md: 0 },
                display: { xs: "none", md: "block" },
              }}
              className={`focus-toggle ${focusModeActive ? "focus-active" : ""}`}
            >
              Fråga {currentQuestionIndex + 1} av{" "}
              {hpTest[currentSectionIndex].questions.length} —{" "}
              {hpTest[currentSectionIndex].questionType}
            </Typography>

            {/* Right section */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: { xs: 0, md: 7 },
                order: { xs: 2, md: 3 },
                width: { xs: "100%", md: "auto" },
                justifyContent: { xs: "space-between", md: "flex-end" },
              }}
            >
              {/* Submit button */}
              {!isMobile && (
                <Button
                  variant="purp"
                  onClick={handleSubmitTest}
                  color="primary"
                  className={`focus-toggle ${focusModeActive ? "focus-active" : ""}`}
                  sx={{
                    fontWeight: 500,
                    borderRadius: 2,
                    p: 0.6,
                    pl: { xs: 2, md: 4 },
                    pr: { xs: 2, md: 4 },
                    fontSize: "0.9rem",
                  }}
                >
                  Lämna in
                </Button>
              )}
              {/* Timer */}
              <Box
                position="relative"
                display="inline-flex"
                className={`focus-toggle ${focusModeActive ? "focus-active" : ""}`}
                onClick={toggleFocusMode}
                style={{ cursor: "pointer" }}
                sx={{ marginRight: { xs: 0, md: 2 } }}
              >
                <CircularProgress
                  variant="determinate"
                  value={(timeLeft / (55 * 60)) * 100}
                  size={60}
                />
                <Tooltip title="Fokusläge">
                  <Box
                    top={0}
                    left={0}
                    bottom={0}
                    right={0}
                    position="absolute"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                  >
                    <Typography
                      variant="caption"
                      component="div"
                      color="text.secondary"
                      ref={timerDisplayRef}
                      sx={{ fontWeight: "500", fontSize: "0.9rem" }}
                    >
                      {`${Math.floor(timeLeftRef.current / 60)}:${timeLeftRef.current % 60 < 10 ? "0" : ""}${timeLeftRef.current % 60}`}
                    </Typography>
                  </Box>
                </Tooltip>
              </Box>

              {/* Drawer button */}
              <Button
                variant="print"
                disableRipple
                onClick={() => setDrawerOpen(true)}
                startIcon={<SegmentIcon />}
                className={`focus-toggle ${focusModeActive ? "focus-active" : ""}`}
                sx={{
                  padding: "8px",
                  mr: { xs: 0, md: 2 },
                  paddingLeft: "14px",
                  paddingRight: "3px",
                  minWidth: "auto",
                  textTransform: "none",
                }}
              />
              <Button
                variant="contained"
                color="primary"
                onClick={handleOpenOverview}
                startIcon={<DatasetIcon />}
                className={`focus-toggle ${focusModeActive ? "focus-active" : ""}`}
                sx={{ display: "none" }}
              >
                Översikt
              </Button>
            </Box>
          </Box>
          <LinearProgress
            className={`focus-toggle ${focusModeActive ? "focus-active" : ""}`}
            variant="determinate"
            value={
              (currentQuestionIndex /
                hpTest[currentSectionIndex].questions.length) *
              100
            }
            sx={{
              display: "none",
              height: 7,
              width: "20%",
              borderRadius: 20,
              margin: "0 auto",
              marginBottom: 0,
              marginTop: "20px",
              "& .MuiLinearProgress-bar": {
                borderRadius: 5,
                backgroundColor: "#8b5cf6",
                transition: "transform 0.5s ease-in-out",
              },
            }}
          />

          {renderQuestion()}
        </Box>
      </Fade>

      {showCodeOverlay && (
        <Box
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();

            // Copy the code to clipboard
            navigator.clipboard
              .writeText(showCodeOverlay)
              .then(() => {
                // Show success snackbar
                showSnackbar("Koden kopierad", "success");

                // Close the overlay
                setShowCodeOverlay(null);
              })
              .catch((err) => {
                console.error("Failed to copy text: ", err);
                showSnackbar("Kunde inte kopiera koden", "error");

                // Still close the overlay even if copy fails
                setShowCodeOverlay(null);
              });
          }}
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            bgcolor: "rgba(0,0,0,0.9)",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
            cursor: "pointer", // Add pointer cursor to indicate clickability
          }}
        >
          <Typography
            variant="body2"
            sx={{
              color: "white",
              position: "absolute",
              bottom: 90,
              mb: 2,
              opacity: 0.7,
              fontWeight: 500,
              fontSize: "1.1rem",
            }}
          >
            Eleverna går in på studera.ai/elev och skriver in provnyckeln{" "}
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: "white",
              mb: 2,
              opacity: 0.7,
              fontWeight: 400,
              fontSize: "3rem",
            }}
          >
            studera.ai/elev
          </Typography>
          <Typography
            variant="h2"
            sx={{
              color: "white",
              fontWeight: 700,
              letterSpacing: "0.1em",
              fontSize: "7rem",
            }}
          >
            {showCodeOverlay}
          </Typography>

          {/* Optional: Add a hint that clicking will copy the code */}
          <Typography
            variant="body2"
            sx={{
              color: "white",
              position: "absolute",
              top: 24,
              right: 24,
              opacity: 0.6,
              fontSize: "0.9rem",
            }}
          >
            Klicka var som helst för att kopiera koden
          </Typography>
        </Box>
      )}

      <WelcomeDialog open={open} onClose={handleClose} user={user} />
    </>
  );
};

export default TestInProgressComponent;
